import React, { useEffect, useRef } from 'react';
import Quote from '../components/Quote';
import { Link } from 'react-router-dom';
import { SiteContent } from '../content';

const Goals: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight + 50) {
        el.classList.add('is-visible');
      } else {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const { hero, seeds, closing } = SiteContent.goals;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper relative overflow-hidden">
      
      {/* SECTION 1: HERO */}
      <section className="pt-40 md:pt-64 pb-20 md:pb-40 text-center px-6 md:px-10 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="animate-on-scroll animate-fade-up">
             <h1 className="section-title text-6xl md:text-8xl lg:text-9xl mb-8 leading-[0.9]">{hero.title}</h1>
          </div>
          <div className="animate-on-scroll animate-fade-up delay-200">
            <p className="text-lg md:text-xl max-w-3xl mx-auto text-[#1A1A1A]/60 font-medium leading-relaxed italic">{hero.subtitle}</p>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <div className="max-w-7xl mx-auto px-4 md:px-10 relative pb-32 md:pb-60">
        <div className="absolute top-0 bottom-0 left-8 md:left-1/2 md:-translate-x-px w-px bg-[#A65D3F]/20 z-0"></div>

        {seeds.map((seed, idx) => {
          const isEven = idx % 2 === 0;
          return (
            <div key={seed.id} className={`relative grid md:grid-cols-2 gap-12 md:gap-24 items-center mb-32 md:mb-56 group ${!isEven ? 'md:text-right' : 'md:text-left'}`}>
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 bg-[#F5F0E6] border-2 border-[#A65D3F] rounded-full z-10"></div>

              <div className={`relative pl-12 md:pl-0 ${!isEven ? 'md:order-2' : 'md:order-1'}`}>
                <div className="animate-on-scroll animate-scale-in">
                  <div className={`relative aspect-square bg-rich-black overflow-hidden shadow-2xl rounded-2xl`}>
                    <img src={seed.image} alt={seed.title} className="w-full h-full object-cover opacity-80" />
                  </div>
                </div>
              </div>

              <div className={`relative pl-12 md:pl-0 ${!isEven ? 'md:order-1 md:pr-12' : 'md:order-2 md:pl-12'}`}>
                <div className="mb-6 animate-on-scroll animate-fade-up delay-100">
                  <Quote text={seed.quote} className="!text-left px-0" />
                </div>
                <div className="animate-on-scroll animate-fade-up delay-200">
                  <h2 className="section-title text-4xl md:text-6xl mb-6">{seed.title}</h2>
                </div>
                <div className="animate-on-scroll animate-fade-up delay-300">
                  <p className="text-lg font-medium text-black/70 mb-10 leading-relaxed max-w-lg inline-block">
                    {seed.description}
                  </p>
                </div>
                <div className="bg-white/60 p-6 md:p-8 rounded-xl border border-[#A65D3F]/20 shadow-lg animate-on-scroll animate-fade-up delay-400">
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-[#A65D3F] mb-6 border-b border-[#A65D3F]/20 pb-4">WHAT THIS BECOMES:</h3>
                  <ul className="space-y-4">
                    {seed.whatItBecomes.map((item, i) => (
                      <li key={i} className={`flex items-center space-x-4 ${!isEven ? 'md:flex-row-reverse md:space-x-reverse' : ''}`}>
                        <div className="w-1.5 h-1.5 rotate-45 bg-[#A65D3F] flex-shrink-0"></div>
                        <span className="text-xs md:text-sm font-bold tracking-wider">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CLOSING (Dark Background) */}
      <section className="section-padding bg-rich-black text-center relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="animate-on-scroll animate-fade-up">
             <h2 className="section-title text-[#F5F0E6] mb-12">{closing}</h2>
             <p className="text-white/60 text-xl italic mb-16">These six seeds grow when you invest in the Sacred Soil Alphabet. Early supporters don't just buy art—they buy a seat at the table as each branch develops.</p>
          </div>
          <div className="animate-on-scroll animate-scale-in delay-200">
            <Link to="/shop" className="btn-primary px-16">Shop the Alphabet</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Goals;